// 🔥 Firebase 가격 관리 고급 유틸리티 - 최고급 구현
// [배경] 가격 관리에 필요한 고급 기능들 (히스토리, 템플릿, 검증, 분석)

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import {
  History as HistoryIcon,
  Assessment as AnalysisIcon,
  ContentCopy as TemplateIcon,
  TrendingUp as TrendIcon,
  CompareArrows as CompareIcon,
  AutoFixHigh as MagicIcon,
  Close as CloseIcon
} from '@mui/icons-material';

import { DeviceGrade, GradePriceInfo } from '../../../../types/gradeSystem';
import { getUnifiedPriceService } from '../../../../services/unifiedPriceAdapter';
import { getGradesForCategory } from '../../../../constants/grades';
import { PriceHistoryEntry } from '../../../../services/unifiedPriceAdapter';

// 🔥 PriceHistoryEntry는 unifiedPriceAdapter에서 import

interface PriceTemplate {
  name: string;
  description: string;
  prices: GradePriceInfo;
  category: 'high' | 'mid' | 'low';
}

// 🔥 가격 템플릿 (브랜드별 기본 가격)
export const PRICE_TEMPLATES: Record<string, PriceTemplate[]> = {
  'Apple-tablet': [
    {
      name: '프리미엄 iPad',
      description: 'iPad Pro 최신 모델용 가격 템플릿',
      prices: { A: 1500000, B: 1200000, C: 900000, D: 600000, E: 300000 },
      category: 'high'
    },
    {
      name: '일반 iPad',
      description: 'iPad Air/기본 모델용 가격 템플릿',
      prices: { A: 800000, B: 650000, C: 450000, D: 250000, E: 120000 },
      category: 'mid'
    },
    {
      name: '미니 iPad',
      description: 'iPad mini 모델용 가격 템플릿',
      prices: { A: 600000, B: 480000, C: 320000, D: 180000, E: 80000 },
      category: 'low'
    }
  ],
  'Samsung-tablet': [
    {
      name: '갤럭시 탭 프리미엄',
      description: 'Galaxy Tab S Ultra 모델용',
      prices: { A: 1200000, B: 950000, C: 700000, D: 450000, E: 200000 },
      category: 'high'
    },
    {
      name: '갤럭시 탭 일반',
      description: 'Galaxy Tab S/A 모델용',
      prices: { A: 650000, B: 520000, C: 350000, D: 200000, E: 90000 },
      category: 'mid'
    }
  ]
};

/**
 * 🔥 가격 히스토리 다이얼로그
 */
interface PriceHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  priceId: string;
  modelName: string;
}

export const PriceHistoryDialog: React.FC<PriceHistoryDialogProps> = ({
  open,
  onClose,
  priceId,
  modelName
}) => {
  const [history, setHistory] = useState<PriceHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 🔥 실제 Firebase에서 가격 히스토리 조회
  const loadHistory = useCallback(async () => {
    if (!open || !priceId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log(`📊 가격 히스토리 로딩: ${priceId}`);
      
      // 🔥 통합 가격 서비스에서 히스토리 조회
      const UnifiedPriceService = getUnifiedPriceService();
      const historyData = await UnifiedPriceService.getPriceHistory(priceId);
      
      console.log(`✅ 가격 히스토리 로딩 완료: ${historyData.length}개`);
      setHistory(historyData);
      
    } catch (err) {
      console.error('❌ 가격 히스토리 로딩 실패:', err);
      setError(err instanceof Error ? err.message : '히스토리 조회 실패');
    } finally {
      setLoading(false);
    }
  }, [open, priceId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // 🔥 날짜 포맷 유틸리티
  const formatDate = (date: Date | any): string => {
    if (!date) return '알 수 없음';
    
    try {
      const d = date instanceof Date ? date : new Date(date);
      if (isNaN(d.getTime())) return '알 수 없음';
      return d.toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '알 수 없음';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon color="primary" />
            가격 변경 히스토리
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="subtitle1" color="text.secondary">
          {modelName}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {/* 🚨 에러 표시 */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              히스토리 조회 중...
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>변경 일시</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>등급</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>이전 가격</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>변경 가격</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>변동률</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>수정자</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.flatMap((entry, entryIdx) =>
                  (entry.changes || []).map((change, idx) => {
                    // 🔥 changedAt 사용 (새 인터페이스)
                    const timestamp = entry.changedAt instanceof Date 
                      ? entry.changedAt 
                      : new Date(entry.changedAt);
                    
                    return (
                      <TableRow key={`${entryIdx}-${idx}`}>
                        <TableCell>
                          {formatDate(timestamp)}
                        </TableCell>
                        <TableCell>
                          <Chip label={`${change.grade}등급`} size="small" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {(change.oldValue || 0).toLocaleString()}원
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                            {(change.newValue || 0).toLocaleString()}원
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TrendIcon 
                              fontSize="small" 
                              color={change.newValue > change.oldValue ? 'success' : 'error'}
                              sx={{ 
                                transform: change.newValue < change.oldValue ? 'rotate(180deg)' : 'none'
                              }}
                            />
                            <Typography 
                              variant="body2" 
                              color={change.newValue > change.oldValue ? 'success.main' : 'error.main'}
                              fontWeight={600}
                            >
                              {change.oldValue > 0 ? 
                                `${(((change.newValue - change.oldValue) / change.oldValue) * 100).toFixed(1)}%` :
                                '신규'
                              }
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={entry.changedBy || 'admin'} size="small" variant="outlined" />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
                
                {history.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                      <Box>
                        <HistoryIcon sx={{ fontSize: 48, color: '#9ca3af', mb: 1 }} />
                        <Typography color="text.secondary" fontWeight={500}>
                          변경 히스토리가 없습니다.
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          가격 변경 시 자동으로 기록됩니다.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * 🔥 가격 템플릿 적용 다이얼로그
 */
interface PriceTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  brand: 'Apple' | 'Samsung';
  deviceType: 'tablet' | 'smartphone';
  selectedPriceIds: string[];
  onApplyTemplate: (template: PriceTemplate) => void;
}

export const PriceTemplateDialog: React.FC<PriceTemplateDialogProps> = ({
  open,
  onClose,
  brand,
  deviceType,
  selectedPriceIds,
  onApplyTemplate
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<PriceTemplate | null>(null);

  const templateKey = `${brand}-${deviceType}`;
  const availableTemplates = PRICE_TEMPLATES[templateKey] || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TemplateIcon color="primary" />
          가격 템플릿 적용
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          선택된 <strong>{selectedPriceIds.length}개 구성</strong>에 템플릿 가격을 적용합니다.
        </Alert>

        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          사용 가능한 템플릿:
        </Typography>

        <Grid container spacing={2}>
          {availableTemplates.map((template, index) => (
            <Grid item xs={12} key={index}>
              <Card 
                variant={selectedTemplate === template ? 'elevation' : 'outlined'}
                sx={{ 
                  cursor: 'pointer',
                  border: selectedTemplate === template ? '2px solid #3b82f6' : undefined,
                  '&:hover': { boxShadow: 2 }
                }}
                onClick={() => setSelectedTemplate(template)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight={600}>
                      {template.name}
                    </Typography>
                    <Chip 
                      label={template.category === 'high' ? '고가' : template.category === 'mid' ? '중가' : '저가'} 
                      color={template.category === 'high' ? 'error' : template.category === 'mid' ? 'warning' : 'success'}
                      size="small"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {template.description}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    {(['A', 'B', 'C', 'D'] as DeviceGrade[]).map(grade => (
                      <Box key={grade} sx={{ textAlign: 'center', minWidth: '80px' }}>
                        <Typography variant="caption" color="text.secondary">
                          {grade}등급
                        </Typography>
                        <Typography variant="body2" fontWeight={600} fontFamily="monospace">
                          {template.prices[grade].toLocaleString()}원
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* 커스텀 템플릿 생성 */}
        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          또는 커스텀 가격 설정:
        </Typography>
        
        <Grid container spacing={2}>
          {(['A', 'B', 'C', 'D'] as DeviceGrade[]).map(grade => (
            <Grid item xs={3} key={grade}>
              <TextField
                fullWidth
                size="small"
                label={`${grade}등급`}
                placeholder="0"
                InputProps={{
                  endAdornment: '원'
                }}
              />
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>취소</Button>
        <Button 
          onClick={() => {
            if (selectedTemplate) {
              onApplyTemplate(selectedTemplate);
              onClose();
            }
          }}
          variant="contained"
          disabled={!selectedTemplate}
          startIcon={<TemplateIcon />}
        >
          템플릿 적용
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * 🔥 가격 분석 다이얼로그
 */
interface PriceAnalysisDialogProps {
  open: boolean;
  onClose: () => void;
  brand: 'Apple' | 'Samsung';
  deviceType: 'tablet' | 'smartphone';
  tableRows: any[];
}

export const PriceAnalysisDialog: React.FC<PriceAnalysisDialogProps> = ({
  open,
  onClose,
  brand,
  deviceType,
  tableRows
}) => {
  // 📊 가격 분석 계산
  const analysis = React.useMemo(() => {
    const rowsWithPrices = tableRows.filter(r => Object.values(r.prices).some(p => p > 0));
    
    if (rowsWithPrices.length === 0) {
      return {
        avgPrices: { A: 0, B: 0, C: 0, D: 0, E: 0 },
        minPrices: { A: 0, B: 0, C: 0, D: 0, E: 0 },
        maxPrices: { A: 0, B: 0, C: 0, D: 0, E: 0 },
        priceRanges: { A: 0, B: 0, C: 0, D: 0, E: 0 },
        recommendations: []
      };
    }

    const grades: DeviceGrade[] = ['A', 'B', 'C', 'D', 'E'];
    const result = {
      avgPrices: {} as GradePriceInfo,
      minPrices: {} as GradePriceInfo,
      maxPrices: {} as GradePriceInfo,
      priceRanges: {} as GradePriceInfo,
      recommendations: [] as string[]
    };

    grades.forEach(grade => {
      const gradeValues = rowsWithPrices.map(r => r.prices[grade]).filter(p => p > 0);
      
      if (gradeValues.length > 0) {
        result.avgPrices[grade] = Math.round(gradeValues.reduce((a, b) => a + b, 0) / gradeValues.length);
        result.minPrices[grade] = Math.min(...gradeValues);
        result.maxPrices[grade] = Math.max(...gradeValues);
        result.priceRanges[grade] = result.maxPrices[grade] - result.minPrices[grade];
      } else {
        result.avgPrices[grade] = 0;
        result.minPrices[grade] = 0;
        result.maxPrices[grade] = 0;
        result.priceRanges[grade] = 0;
      }
    });

    // 가격 추천 생성
    const recommendations = [];
    if (result.priceRanges.A > 500000) {
      recommendations.push('A등급 가격 편차가 큽니다. 가격 정책 검토를 권장합니다.');
    }
    if (result.avgPrices.B / result.avgPrices.A < 0.7) {
      recommendations.push('B등급 가격이 A등급 대비 낮습니다. 가격 비율 조정을 고려하세요.');
    }
    if (rowsWithPrices.length < tableRows.length * 0.5) {
      recommendations.push('가격이 설정되지 않은 모델이 많습니다. 일괄 설정을 권장합니다.');
    }
    
    result.recommendations = recommendations;

    return result;
  }, [tableRows]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AnalysisIcon color="primary" />
          가격 분석 리포트
        </Box>
      </DialogTitle>
      <DialogContent>
        {/* 평균 가격 카드 */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              📊 등급별 평균 가격
            </Typography>
            <Grid container spacing={2}>
              {(['A', 'B', 'C', 'D', 'E'] as DeviceGrade[]).map(grade => (
                <Grid item xs={3} key={grade}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" color="primary" fontWeight={700}>
                      {grade}
                    </Typography>
                    <Typography variant="body1" fontFamily="monospace" fontWeight={600}>
                      {analysis.avgPrices[grade].toLocaleString()}원
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      최소: {analysis.minPrices[grade].toLocaleString()}원
                    </Typography>
                    <br />
                    <Typography variant="caption" color="text.secondary">
                      최대: {analysis.maxPrices[grade].toLocaleString()}원
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        {/* 가격 분포 차트 */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              📈 가격 분포 범위
            </Typography>
            {(['A', 'B', 'C', 'D', 'E'] as DeviceGrade[]).map(grade => (
              <Box key={grade} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {grade}등급
                  </Typography>
                  <Typography variant="body2" fontFamily="monospace">
                    편차: {analysis.priceRanges[grade]?.toLocaleString()}원
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={analysis.maxPrices[grade] ? ((analysis.avgPrices[grade] ?? 0) / (analysis.maxPrices[grade] ?? 1)) * 100 : 0}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: '#f1f5f9',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: grade === 'A' ? '#059669' :
                              grade === 'B' ? '#0ea5e9' :
                              grade === 'C' ? '#f59e0b' :
                              grade === 'D' ? '#ef4444' : '#6b7280'
                    }
                  }}
                />
              </Box>
            ))}
          </CardContent>
        </Card>

        {/* AI 추천 */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              🤖 AI 가격 최적화 추천
            </Typography>
            {analysis.recommendations.length > 0 ? (
              analysis.recommendations.map((rec, index) => (
                <Alert key={index} severity="info" sx={{ mb: 1 }}>
                  {rec}
                </Alert>
              ))
            ) : (
              <Alert severity="success">
                현재 가격 정책이 균형있게 설정되어 있습니다.
              </Alert>
            )}
          </CardContent>
        </Card>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * 🔥 가격 유효성 검증 유틸리티
 */
export const validatePriceData = (prices: GradePriceInfo): {
  isValid: boolean;
  errors: string[];
  warnings: string[];
} => {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  // 기본 유효성 검사
  const grades: DeviceGrade[] = ['A', 'B', 'C', 'D'];
  
  grades.forEach(grade => {
    if (prices[grade] < 0) {
      errors.push(`${grade}등급 가격이 음수입니다.`);
    }
    if (prices[grade] > 10000000) {
      warnings.push(`${grade}등급 가격이 1,000만원을 초과합니다.`);
    }
  });
  
  // 등급별 가격 순서 검증
  if (prices.A < prices.B) {
    warnings.push('A등급이 B등급보다 낮습니다.');
  }
  if (prices.B < prices.C) {
    warnings.push('B등급이 C등급보다 낮습니다.');
  }
  if (prices.C < prices.D) {
    warnings.push('C등급이 D등급보다 낮습니다.');
  }
  
  // 가격 비율 검증
  if (prices.A > 0 && prices.B > 0) {
    const ratio = prices.B / prices.A;
    if (ratio < 0.6 || ratio > 0.9) {
      warnings.push(`B등급이 A등급의 ${(ratio * 100).toFixed(1)}%입니다. (권장: 60-90%)`);
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};
